import React from 'react';
import styled from '@emotion/styled';
import { Box, IconButton, Typography } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PermIdentity from '@mui/icons-material/PermIdentity';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setStep } from '../rcSlice';

const StepContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
`;

const Step = styled(Box)<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ $active }) => ($active ? '#8CCDCD' : '#b0bec5')};
`;

const StepLine = styled(Box)<{ $active?: boolean }>`
  width: 50px;
  height: 2px;
  background-color: ${({ $active }) => ($active ? '#8CCDCD' : '#b0bec5')};
  margin: 0 12px;
`;

const IconButtonStyled = styled(IconButton)<{ $active?: boolean }>`
  background: ${({ $active }) => ($active ? '#8CCDCD' : 'transparent')};
  color: ${({ $active }) => ($active ? 'white' : '#8CCDCD')};
  border: ${({ $active }) => ($active ? '0px' : 'solid 1px #8CCDCD')};
`;

const RcStepperHeader: React.FC = () => {
  const { t } = useTranslation('rc');
  const dispatch = useAppDispatch();
  const step = useAppSelector(state => state.rc.step);
  const lastVerifiedStep = useAppSelector(state => state.rc.lastVerifiedStep);

  const redirectStep = (targetStep: number) => {
    if (targetStep > lastVerifiedStep + 1) return;
    // On dispatch direct le step dans Redux ; RcPage propage ensuite la valeur
    // à l'URL via son useEffect [step] qui préserve les autres query params
    // (?school=, etc.) avec params.toString().
    dispatch(setStep(targetStep));
  };

  return (
    <StepContainer>
      <Step $active={step >= 1}>
        <IconButtonStyled
          $active={step >= 1}
          onClick={() => redirectStep(1)}
          sx={{ cursor: 1 <= lastVerifiedStep + 1 ? 'pointer' : 'default' }}
        >
          <CalendarTodayIcon />
        </IconButtonStyled>
        <Typography variant="caption">{t('layout.step_dates')}</Typography>
      </Step>

      <StepLine $active={step >= 2} />

      <Step $active={step >= 2}>
        <IconButtonStyled
          $active={step >= 2}
          onClick={() => redirectStep(2)}
          sx={{ cursor: 2 <= lastVerifiedStep + 1 ? 'pointer' : 'default' }}
        >
          <PermIdentity />
        </IconButtonStyled>
        <Typography variant="caption">{t('layout.step_info')}</Typography>
      </Step>

      <StepLine $active={step >= 3} />

      <Step $active={step >= 3}>
        <IconButtonStyled
          $active={step >= 3}
          onClick={() => redirectStep(3)}
          sx={{ cursor: 3 <= lastVerifiedStep + 1 ? 'pointer' : 'default' }}
        >
          <DescriptionIcon />
        </IconButtonStyled>
        <Typography variant="caption">{t('layout.step_quote')}</Typography>
      </Step>
    </StepContainer>
  );
};

export default RcStepperHeader;
